import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ChevronDown, ChevronRight, AlertTriangle, TrendingDown, Users, Clock, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CATEGORY_META = {
    dormant:      { label: "Dormant Deals",           color: "text-red-600",   bg: "bg-red-50",   border: "border-red-200",   icon: Clock },
    uncontacted:  { label: "Uncontacted High Matches", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", icon: Users },
    stale:        { label: "Stale Buyer Follow-up",   color: "text-red-600",   bg: "bg-red-50",   border: "border-red-200",   icon: AlertTriangle },
    missing_fees: { label: "Fees Not Set",             color: "text-blue-600",  bg: "bg-blue-50",  border: "border-blue-200",  icon: DollarSign },
};

function fmt(n) {
    if (n == null || n === 0) return "£0";
    return `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
}

function Section({ id, title, count, feeAtRisk, items, open, onToggle, renderItem }) {
    const meta = CATEGORY_META[id];
    const Icon = meta.icon;
    return (
        <div className={`border ${meta.border} rounded-md overflow-hidden`} data-testid={`leakage-section-${id}`}>
            <button
                onClick={onToggle}
                className={`w-full flex items-center justify-between px-5 py-4 ${meta.bg} text-left hover:brightness-95 transition-all`}
                data-testid={`leakage-toggle-${id}`}
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${meta.color} flex-shrink-0`} />
                    <div>
                        <span className={`font-semibold text-sm ${meta.color}`}>{title}</span>
                        <span className="ml-2 text-xs text-muted-foreground font-mono">{count} item{count !== 1 ? "s" : ""}</span>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {feeAtRisk > 0 && (
                        <span className={`text-sm font-mono font-bold ${meta.color}`}>{fmt(feeAtRisk)} at risk</span>
                    )}
                    {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </div>
            </button>
            {open && (
                <div className="divide-y divide-border/60">
                    {items.length === 0 ? (
                        <div className="px-5 py-4 text-sm text-muted-foreground italic">No items in this category.</div>
                    ) : (
                        items.map((item, i) => (
                            <div key={i} className="px-5 py-3.5 flex items-center justify-between gap-4" data-testid={`leakage-item-${id}`}>
                                <div className="flex-1 min-w-0">
                                    {renderItem(item)}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default function Leakage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState({ dormant: true, uncontacted: true, stale: false, missing_fees: false });
    const nav = useNavigate();

    useEffect(() => {
        api.get("/intelligence/leakage")
            .then(r => setData(r.data))
            .catch(() => toast.error("Failed to load leakage data"))
            .finally(() => setLoading(false));
    }, []);

    const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

    if (loading) return (
        <div className="p-8 flex items-center justify-center text-muted-foreground text-sm">
            Computing revenue leakage…
        </div>
    );

    if (!data) return null;

    return (
        <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
            <PageHeader title="Revenue Leakage" subtitle="Fee revenue at risk from inaction across your active pipeline" />

            {/* Gold banner — total at risk */}
            <div className="bg-foreground text-background rounded-md px-6 py-5 flex items-center justify-between" data-testid="leakage-total-banner">
                <div>
                    <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Total fee revenue at risk</div>
                    <div className="text-3xl font-bold font-mono text-gold" data-testid="leakage-total-amount">{fmt(data.total_fee_at_risk)}</div>
                </div>
                <TrendingDown className="w-10 h-10 opacity-20" />
            </div>

            {/* Category 1 — Dormant deals */}
            <Section
                id="dormant"
                title="Dormant Deals"
                count={data.dormant_deals.length}
                feeAtRisk={data.dormant_deals.reduce((s, d) => s + d.fee_at_risk, 0)}
                items={data.dormant_deals}
                open={open.dormant}
                onToggle={() => toggle("dormant")}
                renderItem={(item) => (
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{item.deal_name}</div>
                            <div className="text-xs text-muted-foreground">{item.stage} · <span className="text-red-600 font-medium">{item.days_idle}d idle</span></div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-mono text-red-600">{fmt(item.fee_at_risk)}</span>
                            <Button
                                size="sm" variant="outline"
                                className="h-7 text-xs gap-1"
                                onClick={() => nav(`/deals/${item.deal_id}`)}
                                data-testid="leakage-action-dormant"
                            >
                                Open deal <ArrowRight className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                )}
            />

            {/* Category 2 — Uncontacted matches */}
            <Section
                id="uncontacted"
                title="Uncontacted High Matches"
                count={data.uncontacted_matches.length}
                feeAtRisk={data.uncontacted_matches.reduce((s, m) => s + m.estimated_fee, 0)}
                items={data.uncontacted_matches}
                open={open.uncontacted}
                onToggle={() => toggle("uncontacted")}
                renderItem={(item) => (
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{item.deal_name}</div>
                            <div className="text-xs text-muted-foreground">
                                Match: <span className="font-medium">{item.buyer_name}</span> · score <span className="text-amber-600 font-medium">{item.match_score}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-mono text-amber-600">{fmt(item.estimated_fee)}</span>
                            <Button
                                size="sm" variant="outline"
                                className="h-7 text-xs gap-1"
                                onClick={() => nav(`/contacts/${item.buyer_id}`)}
                                data-testid="leakage-action-uncontacted"
                            >
                                View buyer <ArrowRight className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                )}
            />

            {/* Category 3 — Stale buyer follow-up */}
            <Section
                id="stale"
                title="Stale Buyer Follow-up"
                count={data.stale_buyers.length}
                feeAtRisk={data.stale_buyers.reduce((s, b) => s + b.fee_at_risk, 0)}
                items={data.stale_buyers}
                open={open.stale}
                onToggle={() => toggle("stale")}
                renderItem={(item) => (
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{item.deal_name}</div>
                            <div className="text-xs text-muted-foreground">
                                <span className="font-medium">{item.buyer_name}</span> contacted · <span className="text-red-600 font-medium">{item.days_since_contact}d ago</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-mono text-red-600">{fmt(item.fee_at_risk)}</span>
                            <Button
                                size="sm" variant="outline"
                                className="h-7 text-xs gap-1"
                                onClick={() => nav(`/contacts/${item.buyer_id}`)}
                                data-testid="leakage-action-stale"
                            >
                                View buyer <ArrowRight className="w-3 h-3" />
                            </Button>
                        </div>
                    </div>
                )}
            />

            {/* Category 4 — Missing fees */}
            <Section
                id="missing_fees"
                title="Fees Not Set"
                count={data.missing_fees.length}
                feeAtRisk={0}
                items={data.missing_fees}
                open={open.missing_fees}
                onToggle={() => toggle("missing_fees")}
                renderItem={(item) => (
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{item.deal_name}</div>
                            <div className="text-xs text-muted-foreground">
                                {item.stage} · deal size {fmt(item.deal_size)} · <span className="text-blue-600 font-medium">fee not set</span>
                            </div>
                        </div>
                        <Button
                            size="sm" variant="outline"
                            className="h-7 text-xs gap-1 flex-shrink-0"
                            onClick={() => nav(`/deals/${item.deal_id}?tab=fees`)}
                            data-testid="leakage-action-missing-fee"
                        >
                            Set fee <ArrowRight className="w-3 h-3" />
                        </Button>
                    </div>
                )}
            />
        </div>
    );
}
